import type {
    NoInfer,
    OperationVariables,
    QueryHookOptions,
} from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { useCallback, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';

const IDLE_TIMEOUT = 3 * 60 * 1000;

const usePolledQuery = <
    TData,
    TVariables extends OperationVariables = OperationVariables,
>(
    query: DocumentNode,
    options?: QueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>,
    isCached?: boolean
) => {
    const currentPollInterval = useRef(options?.pollInterval);
    const isCancelled = useRef(false);
    const idleRef = useRef(false);

    const {
        data,
        loading,
        previousData,
        error,
        startPolling,
        stopPolling,
        refetch,
        ...rest
    } = useQuery(query, {
        ...options,
        skipPollAttempt() {
            return idleRef.current;
        },
    });

    useIdleTimer({
        timeout: IDLE_TIMEOUT,
        onIdle: () => {
            idleRef.current = true;
            stopPolling();
        },

        onActive: () => {
            idleRef.current = false;
            if (!options?.skip) {
                void refetch();
            }
            startPolling(options?.pollInterval ?? 360_000);
        },
    });

    const startPollingFn = useCallback(
        (pollInterval: number) => {
            currentPollInterval.current = pollInterval;
            isCancelled.current = false;

            startPolling(currentPollInterval.current);
        },
        [startPolling]
    );

    const stopPollingFn = useCallback(() => {
        isCancelled.current = true;

        stopPolling();
    }, [stopPolling]);

    if (!isCached) {
        return {
            data,
            error,
            loading,
            stopPolling: stopPollingFn,
            startPolling: startPollingFn,
            previousData,
            ...rest,
        };
    }

    const emptyData = !data;
    const isShowPrevious = (emptyData && loading) || options?.skip;

    return {
        ...rest,
        loading,
        error,
        data: isShowPrevious ? previousData : data,
        stopPolling,
        startPolling: startPollingFn,
        previousData,
    };
};

export default usePolledQuery;
